import { useState } from "react";
import { X, Copy, MessageCircle, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { inr, formatApiError } from "../lib/api";
import { STATUS_META, fmtDay, daysText } from "../lib/reminders";
import { logReminder } from "./ReminderRow";

const btn = "flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded-sm transition-colors duration-200";

export const ReminderPreviewModal = ({ row, onClose, onLogged }) => {
  const [text, setText] = useState(row.message);
  const [saving, setSaving] = useState(false);
  const meta = STATUS_META[row.status];

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Message copied"); }
    catch { toast.error("Copy failed — select the text and copy manually"); }
  };
  const confirmSent = async () => {
    setSaving(true);
    try {
      const d = await logReminder(row);
      toast.success(d.duplicate ? "Already recorded a moment ago" : `Reminder #${d.reminders_sent} recorded for ${row.player.name}`);
      onLogged();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center px-4 py-10 overflow-y-auto" onClick={onClose} data-testid="reminder-preview-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 w-full max-w-2xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">WhatsApp Reminder</p>
            <h2 className="font-heading text-xl font-black mt-1">{row.player.name}</h2>
            <p className="text-white/50 text-xs mt-1">
              <span className={`inline-block border px-2 py-0.5 mr-2 text-[10px] uppercase tracking-widest ${meta.cls}`}>{meta.label}</span>
              {inr(row.total)} · due {fmtDay(row.due)} · {daysText(row.days)} · {row.player.guardian_phone || "No phone"}
            </p>
            <p className={`text-xs mt-2 font-semibold ${row.reminders_sent >= 4 ? "text-red-300" : "text-[#D4AF37]"}`} data-testid="reminder-preview-count">
              {row.reminders_sent || 0} reminder{row.reminders_sent === 1 ? "" : "s"} already sent for the invoice due {fmtDay(row.cycle_key)}
            </p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="reminder-preview-close"><X size={20} /></button>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={20}
          className="w-full bg-[#050505] border border-white/15 text-white/85 text-sm p-4 font-mono leading-relaxed focus:outline-none focus:border-[#D4AF37]"
          data-testid="reminder-preview-text" />
        <p className="text-white/30 text-[11px] mt-2">Text between *asterisks* shows in bold on WhatsApp. Nothing is sent automatically — tap "I've Sent It" after sending to add to the count.</p>
        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={copy} className={`${btn} border border-white/20 text-white/80 hover:border-[#D4AF37]`} data-testid="reminder-copy"><Copy size={14} /> Copy</button>
          {row.phone ? (
            <a href={`https://wa.me/${row.phone}?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" className={`${btn} bg-[#D4AF37] text-black hover:bg-[#F3D059]`} data-testid="reminder-open-whatsapp">
              <MessageCircle size={14} /> Open WhatsApp
            </a>
          ) : <span className="text-white/40 text-xs self-center" data-testid="reminder-no-phone">No WhatsApp number on file</span>}
          <button onClick={confirmSent} disabled={saving} className={`${btn} border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black disabled:opacity-40`} data-testid="reminder-confirm-sent">
            <CheckCheck size={14} /> I've Sent It
          </button>
        </div>
      </div>
    </div>
  );
};
